import { useState } from 'react'
import type { BarrierAcknowledgement, BarrierReply, WorkerBarrier } from '../index.js'
import { formatScore } from '../../core/index.js'
import { BarrierReplyForm } from './BarrierReplyForm.js'
import { Button } from '../../core/index.js'
import { Eye, Send } from 'lucide-react'

export function formatBarrierDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const ACKNOWLEDGED_BY: Record<BarrierAcknowledgement['by']['kind'], string> = {
  operator: 'an operator',
  facility_administrator: 'a facility administrator',
}

function acknowledgementLine(acknowledgement: BarrierAcknowledgement, audience: 'worker' | 'staff'): string {
  const who = audience === 'worker' ? 'Seen by your facility' : `Seen by ${ACKNOWLEDGED_BY[acknowledgement.by.kind]}`
  return `${who} · ${formatBarrierDate(acknowledgement.at)}`
}

function replyLine(reply: BarrierReply, audience: 'worker' | 'staff'): string {
  const who = audience === 'worker' ? 'Your facility replied' : `Reply from ${ACKNOWLEDGED_BY[reply.by.kind]}`
  return `${who} · ${formatBarrierDate(reply.at)}`
}

/** What a reader may do to a note: nothing (the worker, the platform admin), or mark seen and reply (staff). */
export interface BarrierActions {
  onAcknowledge?: (barrier: WorkerBarrier) => void
  acknowledging?: string | null
  /** Why the last mark-seen failed, shown under the list. */
  acknowledgeError?: string | null
  /** Sends the facility's answer; absent where the reader may not reply. */
  onReply?: (barrier: WorkerBarrier, message: string) => void
  /** The note whose reply is being sent, while it is. */
  replying?: string | null
  /** Why the last reply failed, and which note it was for, so the error shows under that form alone. */
  replyError?: { barrierId: string; message: string } | null
}

interface BarrierListProps extends BarrierActions {
  barriers: readonly WorkerBarrier[]
  /** Names the worker on each note, for a list that spans a roster. */
  names?: ReadonlyMap<string, string>
  /** How the acknowledgement reads: to the worker, "Seen by your facility"; to staff, who saw it. */
  audience: 'worker' | 'staff'
  empty: string
}

/**
 * A worker's barrier notes, newest first: the behavior and how it stood
 * when they wrote, their words whole, and whether the facility has seen
 * it. One component for the worker's own page, the operator's roster panel,
 * the facility dashboard and the admin's worker detail.
 */
export function BarrierList({ barriers, onAcknowledge, acknowledging = null, acknowledgeError = null, onReply, replying = null, replyError = null, names, audience, empty }: BarrierListProps) {
  // The one note whose reply form is open; it closes on its own once the reply lands, since the note then has one.
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  if (barriers.length === 0) {
    return <div className="text-[11px] text-ink-mid">{empty}</div>
  }
  return (
    <div className="flex flex-col gap-1">
      <ul className="bg-white border border-marine/15 divide-y divide-marine/10">
        {barriers.map((barrier) => {
          const acknowledgement = barrier.acknowledgement
          const open = acknowledgement === null
          const canReply = onReply !== undefined && barrier.reply === null
          const formOpen = canReply && replyingTo === barrier.barrierId
          return (
            <li key={barrier.barrierId} className="px-3 py-2.5 flex flex-col gap-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <div className="text-[11px] font-bold text-ink">
                  {names?.get(barrier.workerId) && <span>{names.get(barrier.workerId)} · </span>}
                  {barrier.standingAt === null ? (
                    <span>General note</span>
                  ) : (
                    <>
                      {barrier.behaviorLabel}
                      <span className="font-normal text-ink-mid"> at {Math.round(barrier.standingAt.value)}</span>
                      {barrier.standingAt.pointsLost !== null && barrier.standingAt.pointsLost > 0 && <span className="font-normal text-coral"> · costing {formatScore(barrier.standingAt.pointsLost)} pts</span>}
                    </>
                  )}
                </div>
                <div className="text-[10px] text-ink-mid whitespace-nowrap">{formatBarrierDate(barrier.submittedAt)}</div>
              </div>
              <blockquote className="text-[12px] text-ink leading-snug whitespace-pre-wrap border-l-2 border-marine/30 pl-2">{barrier.comment}</blockquote>
              <div className="flex items-center justify-between gap-3 text-[10px]">
                {acknowledgement === null ? (
                  <span className="font-bold text-coral">Not seen yet</span>
                ) : (
                  <span className="text-ink-mid">{acknowledgementLine(acknowledgement, audience)}</span>
                )}
                <div className="flex items-center gap-2">
                  {open && onAcknowledge && (
                    <Button icon={Eye} variant="secondary" size="sm" disabled={acknowledging === barrier.barrierId} onClick={() => onAcknowledge(barrier)}>
                      {acknowledging === barrier.barrierId ? 'Marking…' : 'Mark seen'}
                    </Button>
                  )}
                  {canReply && !formOpen && (
                    <Button icon={Send} size="sm" onClick={() => setReplyingTo(barrier.barrierId)}>
                      Reply
                    </Button>
                  )}
                </div>
              </div>
              {barrier.reply && (
                <div className="ml-3 flex flex-col gap-1 border-l-2 border-green/60 pl-2">
                  <div className="text-[10px] text-ink-mid">{replyLine(barrier.reply, audience)}</div>
                  <blockquote className="text-[12px] text-ink leading-snug whitespace-pre-wrap">{barrier.reply.message}</blockquote>
                </div>
              )}
              {formOpen && onReply && (
                <BarrierReplyForm
                  onSend={(message) => onReply(barrier, message)}
                  sending={replying === barrier.barrierId}
                  error={replying === null && replyError?.barrierId === barrier.barrierId ? replyError.message : null}
                  onCancel={() => setReplyingTo(null)}
                />
              )}
            </li>
          )
        })}
      </ul>
      {acknowledgeError && acknowledging === null && <div className="text-[11px] text-coral">{acknowledgeError}</div>}
    </div>
  )
}
